'use client';

/**
 * @fileoverview `DiscoverErrorHost` — the ONE `useDiscoverErrorHandler` on a
 * Discover surface (WEB-1096).
 *
 * Subscribes to the shared `/events/discover` entry (`useDiscoverEventArgs`,
 * so the cold-load single-fetch invariant holds) and owns "this params change
 * was the user's" for every surface — the desktop rail, the mobile drawer,
 * the map takeover, the results bar and the list's reset all change the URL,
 * and the host sees every URL change:
 *
 *   - It arms on every real params transition that changes the request
 *     (`discoverParamsEqual`; List ↔ Map changes no request, so it cannot
 *     fail and does not arm). The revert target is the params before the
 *     transition.
 *   - It skips the transition its own 4xx revert causes, so a revert can
 *     never re-arm into a loop. A revert keeps the current view: only the
 *     request can have failed.
 *   - It clears the flag when the request settles without error, so a later
 *     background failure stays silent.
 *   - The handler toasts and reverts once per error (reference guard), and a
 *     429 never reverts — the empty state owns the countdown.
 *
 * The list arms load more / retry / show all through the context and reports
 * its cursor pages (a cache entry this subscription cannot see) to the same
 * handler. See `lib/discover/discoverErrorHost.ts`.
 */
import * as React from 'react';

import type { CityCode } from '@loci/city-editions';
import { useDiscoverEventsQuery } from '@loci/api';
import { useNotification } from '@loci/store';

import { useDiscoverParams } from '@/hooks/useDiscoverParams';
import {
  DiscoverErrorHostContext,
  useDiscoverRequestKey,
  useReportDiscoverQuery,
  type DiscoverErrorHostValue,
} from '@/lib/discover/discoverErrorHost';
import { useDiscoverErrorHandler, type DiscoverParamsHost } from '@/lib/discover/onDiscoverError';
import { discoverParamsEqual, type DiscoverParams } from '@/lib/discover/url-state';
import { useDiscoverEventArgs } from '@/lib/discover/useDiscoverEventArgs';

export interface DiscoverErrorHostProps {
  /** Active city — keys the shared subscription. */
  cityCode: CityCode;
  children: React.ReactNode;
}

export function DiscoverErrorHost({
  cityCode,
  children,
}: DiscoverErrorHostProps): React.ReactElement {
  const { params, replaceParams } = useDiscoverParams();
  const notifications = useNotification();
  const queryArgs = useDiscoverEventArgs(params, cityCode);
  const { currentData, error, isFetching } = useDiscoverEventsQuery(queryArgs);
  // A transition that leaves the request alone (List ↔ Map) cannot fail.
  const requestKey = useDiscoverRequestKey(queryArgs);

  const currentParamsRef = React.useRef(params);
  currentParamsRef.current = params;
  // The params the host's own 4xx revert is navigating to, until the URL lands.
  const pendingRevertRef = React.useRef<DiscoverParams | null>(null);
  // The handler reads this through a ref, so a fresh object per render is fine.
  const paramsHost: DiscoverParamsHost = {
    replaceParams: (next, options) => {
      // Undo only what failed. `view` is not part of the request, so it
      // cannot be what the backend rejected: a List → Map hop made while the
      // failing request was in flight must survive the revert.
      const target = { ...next, view: currentParamsRef.current.view };
      pendingRevertRef.current = target;
      replaceParams(target, options);
    },
  };
  const { recordParams, handleError, clearUserInitiated, lastError } = useDiscoverErrorHandler({
    notifications,
    paramsHost,
  });

  // Declared before the report effects: when a transition lands on a request
  // that already settled, it must arm first so the settle can clear (success)
  // or toast (error) in the same commit.
  const previousRef = React.useRef({ params, requestKey });
  React.useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = { params, requestKey };
    if (discoverParamsEqual(previous.params, params)) return;
    const revert = pendingRevertRef.current;
    pendingRevertRef.current = null;
    if (revert !== null && discoverParamsEqual(revert, params)) return;
    if (previous.requestKey === requestKey) return;
    recordParams(previous.params, { isUserInitiated: true });
  }, [params, requestKey, recordParams]);

  const reporter = React.useMemo(
    () => ({ handleError, clearUserInitiated }),
    [handleError, clearUserInitiated],
  );
  useReportDiscoverQuery(reporter, { requestKey, error, isFetching, currentData });

  // Load more / retry / show all keep the URL: nothing to revert to.
  const recordUserAction = React.useCallback(
    () => recordParams(null, { isUserInitiated: true }),
    [recordParams],
  );
  const value = React.useMemo<DiscoverErrorHostValue>(
    () => ({ lastError, recordUserAction, reporter }),
    [lastError, recordUserAction, reporter],
  );

  return (
    <DiscoverErrorHostContext.Provider value={value}>{children}</DiscoverErrorHostContext.Provider>
  );
}
